import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Rocket, Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welcome back to DeployForge AI!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded flex items-center justify-center">
              <Rocket className="w-6 h-6 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">
              DeployForge<span className="text-primary">AI</span>
            </span>
          </Link>

          <h1 className="font-heading text-3xl font-bold text-white mb-2">Welcome back</h1>
          <p className="text-foreground/60 mb-8">Sign in to your account to continue deploying</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground/80">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-card border-border focus:border-primary"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground/80">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-card border-border focus:border-primary"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-primary text-black font-bold hover:shadow-neon-cyan"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-foreground/60 mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-[#151929] to-[#0A0E27] items-center justify-center p-8 border-l border-border">
        <motion.div 
          className="max-w-md text-center"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="mb-8">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-float">
              <Rocket className="w-12 h-12 text-primary" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-4">
              Deploy with <span className="text-primary">Intelligence</span>
            </h2>
            <p className="text-foreground/60">
              Let AI analyze your code, predict issues, and optimize your deployments automatically.
            </p>
          </div>

          <div className="bg-black/50 border border-primary/30 rounded-lg p-4 font-code text-sm text-left">
            <div className="text-muted-foreground mb-2">// AI Analysis Complete</div>
            <div className="text-accent">Code Quality: 94/100</div>
            <div className="text-primary">Security: Verified</div>
            <div className="text-white">Ready to deploy</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
